import { ShopLink, ShopLinkType, SHOP_LINK_TYPES } from './ShopLink.types';
import { WebsiteConfiguration } from './WebsiteConfiguration.types';

/**
 * URL segment used for the embedded page of each shop link type.
 * Note: the voucher type historically maps to `/shop` (not `/voucher`).
 */
const SHOP_LINK_SEGMENT: Record<ShopLinkType, string> = {
    voucher: 'shop',
    'snack-shop': 'snack-shop',
    login: 'login'
};

export function getShopLinkSegment(type: ShopLinkType): string {
    return SHOP_LINK_SEGMENT[type];
}

/**
 * Returns the configured shop links in a normalized form.
 *
 * Prefers the new `shopLinks` array, but falls back to the deprecated
 * top-level `voucherUrl`/`useEmbeddedShop` fields so existing cinema
 * configurations keep working without a data migration.
 */
export function getShopLinks(websiteConfiguration?: WebsiteConfiguration): ShopLink[] {
    const shopLinks = websiteConfiguration?.shopLinks;

    if (shopLinks && shopLinks.length > 0) {
        return SHOP_LINK_TYPES.map((type) => shopLinks.find((link) => link.type === type)).filter(
            (link): link is ShopLink => !!link && !!link.url
        );
    }

    const legacyVoucherUrl = websiteConfiguration?.voucherUrl;
    if (legacyVoucherUrl) {
        return [
            {
                type: 'voucher',
                url: legacyVoucherUrl,
                useEmbedded: websiteConfiguration?.useEmbeddedShop
            }
        ];
    }

    return [];
}

/**
 * Resolves the href a shop link should point to. When the link is configured
 * to be embedded it points to the internal embedded page, otherwise it links
 * directly to the configured (external) URL.
 */
export function resolveShopLinkHref(link: ShopLink): string | undefined {
    if (link.useEmbedded) {
        return `/embedded/${getShopLinkSegment(link.type)}`;
    }
    return link.url;
}
